import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { http, apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loading, PriorityDot } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { useTrial } from "@/components/TrialGate";
import SetupChecklist from "@/components/SetupChecklist";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Inbox, AlertTriangle, CheckCircle2, Send, PhoneCall, CalendarClock,
  ThumbsUp, UserCog, Sparkles, ChevronRight, Plus, Clock,
} from "lucide-react";

const KPIS = [
  { key: "new_today", icon: Inbox, to: "/pratiche?status=Nuova richiesta", color: "text-indigo-600 bg-indigo-50" },
  { key: "incomplete", icon: AlertTriangle, to: "/pratiche?incomplete=true", color: "text-amber-600 bg-amber-50" },
  { key: "ready", icon: CheckCircle2, to: "/pratiche?incomplete=false", color: "text-emerald-600 bg-emerald-50" },
  { key: "quotes_to_send", icon: Send, to: "/preventivi", color: "text-sky-600 bg-sky-50" },
  { key: "to_call", icon: PhoneCall, to: "/pratiche", color: "text-rose-600 bg-rose-50" },
  { key: "appointments", icon: CalendarClock, to: "/sopralluoghi", color: "text-violet-600 bg-violet-50" },
  { key: "quotes_accepted", icon: ThumbsUp, to: "/preventivi", color: "text-emerald-600 bg-emerald-50" },
  { key: "requires_operator", icon: UserCog, to: "/inbox", color: "text-red-600 bg-red-50" },
];

function TrialBanner() {
  const trial = useTrial();
  const { t } = useTranslation();
  const s = trial?.status;
  if (!s || !s.is_trial) return null;
  const d = s.days_remaining;
  const tone = d <= 1 ? "bg-red-50 border-red-200 text-red-700" : d <= 2 ? "bg-amber-50 border-amber-200 text-amber-700" : "bg-sky-50 border-sky-200 text-sky-700";
  const msg = d <= 0 ? t("dash.trial_today") : d === 1 ? t("dash.trial_last") : t("dash.trial_remaining", { days: d });
  return (
    <div data-testid="trial-banner" className={`flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-medium ${tone}`}>
      <Clock className="w-4 h-4" /> {msg}
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const nav = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const isTenantAdmin = user?.role === "TENANT_ADMIN";

  const load = () => http.get("/dashboard").then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);

  const seedDemo = async () => {
    try { await http.post("/demo/seed"); toast.success(t("dash.demo_created")); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!data) return <Loading />;
  const dot = { red: "Alta", orange: "Media", green: "Bassa" };

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-6">
      <TrialBanner />
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-[#0F172A]">{t("dash.greeting")} – {data.greeting_name} 👋</h1>
          <p className="text-slate-500 text-sm mt-1">{t("dash.subtitle")}</p>
        </div>
        <div className="flex gap-2">
          {isTenantAdmin && (
            <Button data-testid="seed-demo-btn" variant="outline" onClick={seedDemo} className="gap-1.5">
              <Sparkles className="w-4 h-4" /> {t("dash.demo")}
            </Button>
          )}
          <Button data-testid="dash-new-quote" onClick={() => nav("/pratiche")} className="bg-[#0F172A] hover:bg-[#1E293B] gap-1.5">
            <Plus className="w-4 h-4" /> {t("dash.new_quote")}
          </Button>
        </div>
      </div>

      <SetupChecklist isAdmin={isTenantAdmin} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4" data-testid="dash-kpi-grid">
        {KPIS.map((k) => (
          <Card key={k.key} data-testid={`kpi-${k.key}`} onClick={() => nav(k.to)}
            className="p-4 cursor-pointer hover:shadow-md transition-shadow border-slate-200">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${k.color}`}><k.icon className="w-5 h-5" /></div>
            <div className="mt-3 text-3xl font-heading font-extrabold text-[#0F172A]">{data.kpi[k.key] ?? 0}</div>
            <div className="text-xs text-slate-500 mt-0.5">{t(`dash.kpi_${k.key}`)}</div>
          </Card>
        ))}
      </div>

      <div>
        <h2 className="font-heading text-lg font-bold text-[#0F172A] mb-3">{t("dash.attention_title")}</h2>
        <Card className="divide-y divide-slate-100 border-slate-200">
          {data.attention.length === 0 && <div className="p-6 text-sm text-slate-400 text-center">{t("dash.attention_empty")}</div>}
          {data.attention.map((a) => (
            <button key={a.project_id} data-testid="attention-item" onClick={() => nav(`/pratiche/${a.project_id}`)}
              className="w-full flex items-center gap-3 p-4 hover:bg-slate-50 text-left">
              <PriorityDot priority={dot[a.color] || "Media"} />
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-[#0F172A] truncate">{a.customer_name}</div>
                <div className="text-sm text-slate-500 truncate">{a.reason}</div>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
            </button>
          ))}
        </Card>
      </div>
    </div>
  );
}
